/**
 * Authentication context for Forensic Accountants
 * Separate from Clerk authentication used for main users
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from '@/lib/http';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface AccountantProfile {
  id: string;
  email: string;
  full_name: string;
  firm_name: string;
  license_number?: string;
  phone?: string;
  created_at: string;
  last_login?: string;
  status: string;
}

interface AccountantAuthContextType {
  accountant: AccountantProfile | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  getAuthHeader: () => { Authorization: string } | {};
}

interface RegisterData {
  email: string;
  password: string;
  full_name: string;
  firm_name: string;
  license_number?: string;
  phone?: string;
  // Security (audit C-5, 2026-04-18): accountant signup is invite-only.
  // The token is emailed to the invitee when a user creates a share in
  // /collaboration/shares and must be echoed back on registration.
  invite_token: string;
}

const AccountantAuthContext = createContext<AccountantAuthContextType | undefined>(undefined);

function readStoredSession(): { token: string | null; accountant: AccountantProfile | null } {
  const storedToken = localStorage.getItem('accountant_token');
  const storedAccountant = localStorage.getItem('accountant_profile');
  if (storedToken && storedAccountant) {
    try {
      const parsed = JSON.parse(storedAccountant) as AccountantProfile;
      return { token: storedToken, accountant: parsed };
    } catch {
      // Corrupted storage — clear it and treat as signed out
      localStorage.removeItem('accountant_token');
      localStorage.removeItem('accountant_profile');
    }
  }
  return { token: null, accountant: null };
}

export function AccountantAuthProvider({ children }: { children: React.ReactNode }) {
  // Single lazy init: readStoredSession() runs exactly once, synchronously.
  const [{ accountant, token }, setAuth] = useState<{ accountant: AccountantProfile | null; token: string | null }>(
    () => readStoredSession(),
  );
  // Hydration is synchronous — loading is always false after the initializer
  // (signed in, signed out, or corrupted storage).
  const loading = false;

  const login = async (email: string, password: string) => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/v1/collaboration/login`, {
        email,
        password
      });

      const { access_token, accountant: accountantProfile } = response.data;

      // Store in state
      setAuth({ accountant: accountantProfile, token: access_token });

      // Persist to localStorage
      localStorage.setItem('accountant_token', access_token);
      localStorage.setItem('accountant_profile', JSON.stringify(accountantProfile));

    } catch (error: any) {
      console.error('Login failed:', error);
      throw new Error(error.response?.data?.detail || 'Login failed');
    }
  };

  const register = async (data: RegisterData) => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/v1/collaboration/register`, data);

      const { access_token, accountant: accountantProfile } = response.data;

      // Store in state
      setAuth({ accountant: accountantProfile, token: access_token });

      // Persist to localStorage
      localStorage.setItem('accountant_token', access_token);
      localStorage.setItem('accountant_profile', JSON.stringify(accountantProfile));

    } catch (error: any) {
      console.error('Registration failed:', error);
      throw new Error(error.response?.data?.detail || 'Registration failed');
    }
  };

  const logout = () => {
    setAuth({ accountant: null, token: null });
    localStorage.removeItem('accountant_token');
    localStorage.removeItem('accountant_profile');
  };

  const getAuthHeader = () => {
    if (token) {
      return { Authorization: `Bearer ${token}` };
    }
    return {};
  };

  return (
    <AccountantAuthContext.Provider 
      value={{ 
        accountant, 
        token, 
        loading, 
        login, 
        register, 
        logout,
        getAuthHeader
      }}
    >
      {children}
    </AccountantAuthContext.Provider>
  );
}

export function useAccountantAuth() {
  const context = useContext(AccountantAuthContext);
  if (context === undefined) {
    throw new Error('useAccountantAuth must be used within AccountantAuthProvider');
  }
  return context;
}
